"use client";

/**
 * Activity card for the canvas activities renderer.
 * Shows image/gradient header, label badges, time slot info, signup counts, and CTA.
 */

import type React from "react";
import { memo, useCallback, useMemo, useState } from "react";
import { Calendar, Check, ExternalLink, Link, MapPin, Settings, Trash2, Users } from "lucide-react";
import { formatDateShort, formatTimeRange } from "@/lib/utils/formatting";
import { getTimezoneSuffix } from "@/lib/utils/timezones";
import { FetchPill } from "@/fetch-ui/FetchPill";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import type { ActivityLabelRow, ActivityRow, ActivityTimeSlotRow, SignupCounts } from "../../../_logic/types/db-types";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useCanvasFieldClick } from "../../../_hooks/useCanvasFieldClick";
import { useTimeZone } from "../../../_hooks/useTimeZone";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { KebabMenu } from "../../_shared/KebabMenu";
import type { MenuGroup } from "../../_shared/menu-types";
import {
  ACTIVITY_DEEPLINK_PARAM,
  CAPACITY_AVAILABLE_COLOR,
  CAPACITY_FULL_COLOR,
  CAPACITY_FULL_RATIO,
  CAPACITY_NEARLY_FULL_COLOR,
  CAPACITY_NEARLY_FULL_RATIO,
  CARD_IMAGE_RENDER_WIDTH,
  DAY_CAP_BLOCK_LABEL,
  EXCLUSION_GROUP_BLOCK_LABEL,
  LANDING_IMAGE_RENDER_QUALITY,
} from "../../../_logic/constants";
import { transformStorageImageUrl } from "../../../_logic/image-cdn";
import { buildLandingPageSettingsPath } from "@/lib/routes/landing-page-manage-paths";
import { useToast } from "@/components/providers/FetchToastProvider";
import { isActivitySignupClosed, type RuleBlockReason } from "../../../_logic/utils/activity-signup-status";
import { InlineTextEdit } from "./_shared/InlineTextEdit";

export interface ActivityCardProps {
  activity: ActivityRow;
  counts: SignupCounts | undefined;
  labels: ActivityLabelRow[];
  timeSlots: ActivityTimeSlotRow[];
  isReadOnly?: boolean;
  /** Parent component ID for canvas click-to-focus (builder only) */
  componentId?: string;
  onDelete: (activity: ActivityRow) => void;
  onSignup: (activity: ActivityRow) => void;
  /** Public view: open the cancel/manage flow for an activity the viewer is registered for. */
  onManage?: (activity: ActivityRow) => void;
  /** Callback to update the activity's meeting location inline (builder only) */
  onUpdateLocation?: (activity: ActivityRow, location: string) => void;
  /**
   * Client-side day-cap/exclusion-group hint, from useActivityRuleState — set only when EVERY
   * bookable slot of this activity is blocked. Additive: checked after the existing already-
   * signed-up states and before isSignupsClosed, per the existing precedence.
   */
  ruleBlock?: RuleBlockReason;
}

/** Renders a single activity card matching the settings ActivityCard layout */
function ActivityCardComponent({
  activity,
  counts,
  labels,
  timeSlots,
  isReadOnly,
  componentId,
  onDelete,
  onSignup,
  onManage,
  onUpdateLocation,
  ruleBlock,
}: ActivityCardProps): React.ReactElement {
  const { primaryColor } = useBuilderContext();
  const {
    isPublic,
    isOrganizer,
    slug,
    activitySignupMode,
    canPreviewTestPublish,
    signedUpActivityIds,
    registrationBySlotId,
  } = usePublicViewContext();
  const { showToast } = useToast();
  const timeZone = useTimeZone();
  const fieldClick = useCanvasFieldClick(componentId ?? "");
  // Derived values
  const matchedLabels = activity.label_ids.length > 0 ? labels.filter((l) => activity.label_ids.includes(l.id)) : [];
  const buttonTextColor = getButtonTextColor(primaryColor);

  const timeSlotDisplay = useMemo(() => {
    if (timeSlots.length === 0) return null;

    const firstSlot = timeSlots[0];
    const datePart = firstSlot.date ? formatDateShort(firstSlot.date) : null;
    const tzSuffix = getTimezoneSuffix(timeZone, firstSlot.date);

    if (timeSlots.length === 1) {
      const timePart = firstSlot.start_time
        ? `${formatTimeRange(firstSlot.start_time, firstSlot.end_time)}${tzSuffix}`
        : null;

      if (datePart && timePart) return `${datePart}, ${timePart}`;
      return datePart || timePart;
    }

    // Show "Multiple dates" if dates differ across slots
    const allSameDate = timeSlots.every((s) => s.date === firstSlot.date);
    const dateLabel = allSameDate ? datePart : "Multiple dates";
    return dateLabel ? `${dateLabel} — ${timeSlots.length} time slots` : `${timeSlots.length} time slots`;
  }, [timeSlots, timeZone]);

  const firstLabelColor = matchedLabels.length > 0 ? matchedLabels[0].color : null;
  const [imgError, setImgError] = useState(false);

  const gradientStyle = {
    background: firstLabelColor
      ? `linear-gradient(135deg, ${firstLabelColor}99, ${firstLabelColor}33)`
      : `linear-gradient(135deg, ${primaryColor}99, ${primaryColor}33)`,
  };

  const registered = counts?.registered_count ?? 0;

  // Capacity bar (internal, bounded-capacity activities only). The numeric count above the bar
  // already conveys the value to screen readers, so the bar itself is decorative (aria-hidden).
  const totalCapacity = counts?.total_capacity ?? null;
  const hasCapacityBar = activitySignupMode === "internal" && totalCapacity !== null && totalCapacity > 0;
  const capacityRatio = hasCapacityBar && totalCapacity ? registered / totalCapacity : 0;
  const capacityFillPct = Math.min(100, Math.round(capacityRatio * 100));
  // Escalating capacity meter (decoupled from brand color so it always reads green→amber→red as it fills):
  // green while there's room, amber when nearly full, red when full.
  const capacityBarColor =
    capacityRatio >= CAPACITY_FULL_RATIO
      ? CAPACITY_FULL_COLOR
      : capacityRatio >= CAPACITY_NEARLY_FULL_RATIO
        ? CAPACITY_NEARLY_FULL_COLOR
        : CAPACITY_AVAILABLE_COLOR;

  // Determine CTA state for public view
  const isSignupsClosed = isActivitySignupClosed(activity);
  const isFull =
    counts?.total_capacity !== null && counts?.total_capacity !== undefined && registered >= counts.total_capacity;
  const slotsWithLinks = useMemo(() => timeSlots.filter((s) => s.booking_link), [timeSlots]);
  const hasSignup = activity.signup_enabled;

  // Holding ANY slot of an activity is being registered for it, and that is all the card says.
  // It previously demanded EVERY slot before showing the pill, so an attendee holding one wave of
  // a three-wave excursion fell through to a bare "Sign up for another slot" button and read as
  // unregistered. That extra action is gone rather than merely demoted: signing up for a second
  // slot is refused server-side anyway wherever a day cap or an exclusion group applies
  // (signup_block_reason NW101/NW102 — an exclusion group counts the viewer's existing row on
  // THIS activity), so offering it led to a rejection.
  const restrictionMode = activity.signup_restriction_mode ?? "none";
  const isSignedUpForActivity = signedUpActivityIds.has(activity.id);

  // Manage/cancel affordance. Only session-proven viewers hold registration ids (the credential
  // the cancel action authorizes against), so its presence gates the inline link — anonymous
  // viewers keep the static pill and cancel via the email link.
  const cancelableRegisteredCount = useMemo(
    () => timeSlots.filter((s) => registrationBySlotId.has(s.id)).length,
    [timeSlots, registrationBySlotId]
  );
  const canManage = isPublic && cancelableRegisteredCount > 0 && Boolean(onManage);
  const bookableCount = useMemo(() => timeSlots.filter((s) => s.is_bookable).length, [timeSlots]);
  const manageLinkLabel =
    cancelableRegisteredCount > 1 ? "Manage my times" : bookableCount > 1 ? "Manage my time" : "Cancel registration";
  // Compact label for the inline single-row footer button (full label stays as the accessible name).
  const manageActionLabel = cancelableRegisteredCount > 1 || bookableCount > 1 ? "Manage" : "Cancel";

  // Organizer menu (public view only)
  const showOrganizerMenu = isPublic && isOrganizer;

  // "Test" badge — only enterprise viewers ever see a test_publish activity at all, so seeing it
  // means they're allowed to; the badge just makes clear it isn't live to the public yet.
  const showTestBadge = isPublic && activity.test_publish && canPreviewTestPublish;

  /** Copy a shareable link that deep-opens this activity's signup modal on the public page. */
  const handleCopyLink = useCallback(async () => {
    const url = `${window.location.origin}${window.location.pathname}?${ACTIVITY_DEEPLINK_PARAM}=${activity.id}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast("Link copied", "success");
    } catch (error) {
      console.error("ActivityCard: failed to copy activity link", error);
      showToast("Failed to copy link", "error");
    }
  }, [activity.id, showToast]);

  const organizerMenuGroups: MenuGroup[] = useMemo(
    () =>
      showOrganizerMenu
        ? [
            [
              {
                icon: Link,
                label: "Copy link",
                onClick: handleCopyLink,
              },
              {
                icon: Settings,
                label: "Manage activity",
                onClick: () =>
                  window.open(
                    buildLandingPageSettingsPath(slug, {
                      tab: "activities",
                      query: { activity: activity.id },
                    }),
                    "_blank"
                  ),
              },
            ],
          ]
        : [],
    [showOrganizerMenu, slug, activity.id, handleCopyLink]
  );

  const imageSrc = useMemo(
    () =>
      activity.image
        ? transformStorageImageUrl(activity.image, {
            width: CARD_IMAGE_RENDER_WIDTH,
            quality: LANDING_IMAGE_RENDER_QUALITY,
          })
        : "",
    [activity.image]
  );

  return (
    <div
      className={`group/card relative flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition-[transform,box-shadow] ${
        isPublic
          ? "[@media(hover:hover)]:hover:-translate-y-0.5 [@media(hover:hover)]:hover:shadow-md"
          : componentId
            ? "cursor-pointer hover:shadow-md"
            : ""
      }`}
      onClick={!isPublic && componentId ? fieldClick(`activities-item:${activity.id}`) : undefined}
    >
      {/* Image / Gradient header — decorative; long-press save and tap-to-zoom intentionally disabled */}
      <div className="relative h-40 w-full bg-gray-100">
        {activity.image && !imgError ? (
          <img
            src={imageSrc}
            alt=""
            className="h-full w-full select-none object-cover"
            loading="lazy"
            decoding="async"
            onError={() => setImgError(true)}
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
            style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", pointerEvents: "none" }}
          />
        ) : (
          <div className="h-full w-full" style={gradientStyle} />
        )}
        {/* Faint bottom gradient — keeps label pills legible over bright images */}
        {matchedLabels.length > 0 && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-black/40 to-transparent"
            aria-hidden="true"
          />
        )}
        {/* Test-publish badge — enterprise viewers only, see showTestBadge above */}
        {showTestBadge && (
          <div className="absolute left-2 top-2">
            <FetchPill as="span" size="sm" color="#7c3aed" solid className="backdrop-blur-sm">
              Test
            </FetchPill>
          </div>
        )}
        {/* Label badge overlays */}
        {matchedLabels.length > 0 && (
          <div className="absolute bottom-2 left-2 flex gap-1">
            {matchedLabels.map((label) => (
              <FetchPill
                key={label.id}
                as="span"
                size="sm"
                color={label.color || undefined}
                solid
                className="backdrop-blur-sm"
              >
                {label.name}
              </FetchPill>
            ))}
          </div>
        )}
      </div>

      {/* Delete button (builder hover) */}
      {!isReadOnly && !isPublic && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(activity);
          }}
          className="absolute right-2 top-2 rounded-full bg-black/50 p-1.5 text-white opacity-0 transition-opacity hover:bg-black/70 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 group-hover/card:opacity-100"
          aria-label={`Delete ${activity.title}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Organizer menu (public view only) */}
      {showOrganizerMenu && (
        <div className="absolute right-2 top-2">
          <KebabMenu groups={organizerMenuGroups} triggerLabel={`Options for ${activity.title}`} />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-base font-semibold text-gray-900">{activity.title}</h3>

        {/* Date / Time slot info — emphasized; it's the primary thing attendees scan for */}
        {timeSlotDisplay && (
          <div className="mt-1.5 flex items-start gap-1.5 text-sm font-semibold text-gray-900">
            <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
            <span>{timeSlotDisplay}</span>
          </div>
        )}

        {/* Location */}
        {(!isPublic || activity.meeting_location) && (
          <p className="mt-1.5 truncate text-sm font-medium text-gray-900">
            <MapPin className="mr-1.5 inline-block h-4 w-4 align-[-3px]" aria-hidden="true" />
            {!isPublic && componentId && onUpdateLocation ? (
              <InlineTextEdit
                tag="span"
                value={activity.meeting_location || ""}
                placeholder="Add location"
                className="text-sm font-medium text-gray-900"
                componentId={componentId}
                fieldId={`activity-location:${activity.id}`}
                onCommit={(value) => onUpdateLocation(activity, value)}
              />
            ) : (
              activity.meeting_location
            )}
          </p>
        )}

        {/* Signup meta (count + capacity bar) — sits directly below date/location regardless of
            whether a capacity bar renders, instead of being flex-pushed to the content div's
            bottom edge (which left a large gap above it whenever there was no bar). */}
        <div className="mt-1.5">
          {/* Signup count */}
          {counts && activitySignupMode === "internal" && (
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
              <Users className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                {counts.total_capacity !== null
                  ? `${counts.registered_count} / ${counts.total_capacity}`
                  : `${counts.registered_count} signed up`}
                {counts.waitlisted_count > 0 && ` (${counts.waitlisted_count} waitlisted)`}
              </span>
            </div>
          )}
          {/* Capacity bar — visual fill of the count above. Turns amber once nearly full. */}
          {hasCapacityBar && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${capacityFillPct}%`, backgroundColor: capacityBarColor }}
              />
            </div>
          )}
          {counts?.total_capacity != null && activitySignupMode === "external" && (
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
              <Users className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Capacity: {counts.total_capacity}</span>
            </div>
          )}
        </div>
      </div>

      {/* CTA button (public view only) */}
      {isPublic && (
        <div className="px-5 pb-5">
          {isSignedUpForActivity ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-green-50 px-3 py-3 text-center text-sm font-semibold text-green-700">
                <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
                {restrictionMode === "single_choice" ? "Signed up" : "Registered"}
              </span>
              {canManage && (
                <button
                  type="button"
                  onClick={() => onManage?.(activity)}
                  aria-label={manageLinkLabel}
                  className="shrink-0 touch-manipulation rounded-xl border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-600 transition-colors [@media(hover:hover)]:hover:bg-gray-50"
                >
                  {manageActionLabel}
                </button>
              )}
            </div>
          ) : ruleBlock && !isFull && slotsWithLinks.length === 0 ? (
            // The day-cap/exclusion-group rule only applies to the registered path — a full
            // slot's only available action is "Join Waitlist" (unaffected by the rule), and an
            // external booking link bypasses our registration flow entirely, so neither should
            // be hidden behind this hint. Still opens the modal on click (rather than a static,
            // non-interactive pill) so the viewer can see the same reason spelled out there.
            <button
              type="button"
              onClick={() => onSignup(activity)}
              aria-label={`${ruleBlock === "day_cap" ? DAY_CAP_BLOCK_LABEL : EXCLUSION_GROUP_BLOCK_LABEL} — view details`}
              className="block w-full touch-manipulation rounded-xl bg-gray-100 px-3 py-3 text-center text-sm font-medium text-gray-600 transition-colors [@media(hover:hover)]:hover:bg-gray-200"
            >
              {ruleBlock === "day_cap" ? DAY_CAP_BLOCK_LABEL : EXCLUSION_GROUP_BLOCK_LABEL}
            </button>
          ) : isSignupsClosed ? (
            <span className="block rounded-xl bg-gray-100 px-3 py-3 text-center text-sm font-medium text-gray-400">
              Signups Closed
            </span>
          ) : slotsWithLinks.length === 1 ? (
            <a
              href={slotsWithLinks[0].booking_link!}
              target="_blank"
              rel="noopener noreferrer"
              className="flex touch-manipulation items-center justify-center gap-1.5 rounded-xl px-3 py-3 text-center text-sm font-semibold transition-opacity [@media(hover:hover)]:hover:opacity-90"
              style={{ backgroundColor: primaryColor, color: buttonTextColor }}
            >
              Book Now
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          ) : slotsWithLinks.length > 1 ? (
            <button
              type="button"
              onClick={() => onSignup(activity)}
              className="flex w-full touch-manipulation items-center justify-center gap-1.5 rounded-xl px-3 py-3 text-sm font-semibold transition-opacity [@media(hover:hover)]:hover:opacity-90"
              style={{ backgroundColor: primaryColor, color: buttonTextColor }}
            >
              Book Now
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : hasSignup ? (
            <button
              type="button"
              onClick={() => onSignup(activity)}
              className="w-full touch-manipulation rounded-xl px-3 py-3 text-sm font-semibold transition-opacity [@media(hover:hover)]:hover:opacity-90"
              style={{ backgroundColor: primaryColor, color: buttonTextColor }}
            >
              {isFull ? "Join Waitlist" : "Sign Up"}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Memoized so a signup/cancel that updates context doesn't re-render every card in the grid —
 *  only cards whose props actually changed re-render. Relies on stable handler props from the
 *  renderer (see `onManage`/`onSignup` ref-stabilization in ActivitiesRenderer). */
export const ActivityCard = memo(ActivityCardComponent);
